/// <reference types="vite/client" />
import { DEFAULT_LOCALE } from '@longy/common'
import { Toaster } from '@longy/web-components'
import { CmsSiteKey } from '@prisma-clinic/client'
import type { QueryClient } from '@tanstack/react-query'
import { QueryClientProvider } from '@tanstack/react-query'
import {
    createRootRouteWithContext,
    HeadContent,
    Link,
    Outlet,
    Scripts,
    useMatches,
} from '@tanstack/react-router'
import i18n from 'i18next'
import React, { Suspense } from 'react'
import { useTranslation } from 'react-i18next'

import { GoogleTagManager } from '../components/analytics/GoogleTagManager'
import { MetaPixel } from '../components/analytics/MetaPixel'
import { CookieConsent } from '../components/cookie-consent/CookieConsent'
import type { ApiUtils } from '../core/trpc'
import { api, createTRPCClient } from '../core/trpc'
import indexCss from '../index.css?url'
import { TranslationProvider } from '../providers/TranslationProvider'

const TanStackRouterDevtools =
    process.env.NODE_ENV === 'production'
        ? () => null
        : React.lazy(() =>
              import('@tanstack/router-devtools')
                  .then((res) => ({
                      default: res.TanStackRouterDevtools,
                  }))
                  // Suspense does not catch a rejected lazy import, and the nearest boundary is the
                  // router root — so when the dev server re-optimizes deps and this chunk's stale
                  // ?v= hash 404s, a devtools-only failure replaces the entire page with an error.
                  // Losing the devtools panel is the correct blast radius for that.
                  .catch(() => ({ default: () => null })),
          )

export interface RouterContext {
    queryClient: QueryClient
    trpcClient: ReturnType<typeof createTRPCClient>
    apiUtils: ApiUtils
}

export const Route = createRootRouteWithContext<RouterContext>()({
    head: () => ({
        meta: [
            { charSet: 'utf-8' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
            { property: 'og:site_name', content: 'Long-Y' },
        ],
        links: [
            { rel: 'stylesheet', href: indexCss },
            { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
            { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
            {
                rel: 'preconnect',
                href: 'https://fonts.gstatic.com',
                crossOrigin: 'anonymous' as const,
            },
            {
                rel: 'stylesheet',
                href: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&display=swap',
            },
        ],
    }),
    loader: async ({ context }) => {
        await Promise.all([
            context.apiUtils.translation.getJson.ensureData().catch(() => undefined),
            /*
             * Warmed here rather than per route, because the header renders on every page. Only the
             * default locale is warmed: that is what server rendering emits, and a visitor who
             * switched language already refetches everything on the switch.
             */
            context.apiUtils.cmsPage.getSiteHeader
                .ensureData({ siteKey: CmsSiteKey.LONGY, locale: DEFAULT_LOCALE })
                .catch(() => undefined),
        ])
    },
    component: RootComponent,
    notFoundComponent: NotFound,
})

function NotFound() {
    const { t } = useTranslation()

    return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
            <h1 className="text-4xl font-bold text-brand-dark">404</h1>
            <p className="text-lg text-brand-gray">{t('frontend.cms.notFoundTitle')}</p>
            <p className="text-brand-gray">{t('frontend.cms.notFoundBody')}</p>
            <Link
                to="/"
                className="font-bold text-primary transition-colors hover:text-brand-yellow"
            >
                {t('frontend.cms.backHome')}
            </Link>
        </div>
    )
}

function RootComponent() {
    const { queryClient, trpcClient } = Route.useRouteContext()

    return (
        <RootDocument>
            <api.Provider client={trpcClient} queryClient={queryClient}>
                <QueryClientProvider client={queryClient}>
                    <TranslationProvider>
                        <Outlet />
                        <MetaPixel />
                        <GoogleTagManager />
                        <CookieConsent />
                        <Suspense fallback={null}>
                            <TanStackRouterDevtools position="bottom-right" />
                        </Suspense>
                        <Toaster />
                    </TranslationProvider>
                </QueryClientProvider>
            </api.Provider>
        </RootDocument>
    )
}

/**
 * The language of the document.
 *
 * A cms page is served from a url that belongs to one locale, so its content language is known on
 * the server and does not depend on what the visitor last picked — reading it off the matched
 * route is what keeps `lang` honest for the text that is actually on the page. Everywhere else the
 * chrome's language stands, which SSR renders as the default and the client may change.
 */
const useDocumentLang = (): string => {
    const matches = useMatches()

    for (const match of matches) {
        const data = match.loaderData as { page?: { locale?: string } | null } | undefined
        if (data?.page?.locale) return data.page.locale
    }

    return i18n.isInitialized ? i18n.language : DEFAULT_LOCALE
}

function RootDocument({ children }: { children: React.ReactNode }) {
    // For everything but a cms page this differs between server and client for a visitor who
    // picked English — which is exactly what the suppressHydrationWarning below is for.
    const lang = useDocumentLang()

    return (
        <html suppressHydrationWarning lang={lang}>
            <head>
                <HeadContent />
            </head>
            <body>
                {children}
                <Scripts />
            </body>
        </html>
    )
}
